import React, { useEffect, useState, useContext } from "react";
import { Link } from "react-router-dom";
import { PiListMagnifyingGlass, PiTrash } from "react-icons/pi";
import "../styles/BookingPage.css";
import { QRCodeCanvas } from "qrcode.react";
import { AuthContext } from "../components/AuthContext";
import { subscribeBookingsForUser, deleteBooking, getBookingsForUser, removeBookedSeatsFromSchedule } from "../config/firebaseService";

const BookingPage = ({ userId }) => {
  // State variables for bookings, loading state, and errors
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [completedBookings] = useState(new Set());
  const { user } = useContext(AuthContext);

  // Optionally load completed bookings from server in future

  // Fetch user bookings from Firestore (real-time)
  useEffect(() => {
    const uid = userId || user?.id;
    if (!uid) {
      setLoading(false);
      setBookings([]);
      return;
    }
  setLoading(true);
  setError(null);

    // Prefer real-time subscription; include error handler and fallback
    const unsub = subscribeBookingsForUser(
      uid,
      (items) => {
        setBookings(items);
        setLoading(false);
      }
    );

    // Safety: also attempt a one-shot load shortly after, in case sub fails silently
  getBookingsForUser(uid)
      .then((items) => {
        // If subscription already delivered, this will simply overwrite with same data
        setBookings(items);
      })
      .catch((e) => {
    console.error("One-shot booking load failed:", e);
    setError(e?.message || "Failed to load bookings");
      })
      .finally(() => setLoading(false));

    return () => {
      if (typeof unsub === "function") unsub();
    };
  }, [userId, user]);

  // Optionally persist completed bookings to server later

  // Show loading message if bookings are still being fetched
  if (loading) {
    return <p>Loading bookings...</p>;
  }
  if (error) {
    return <p>Error: {error}</p>;
  }

  // Done button removed; deletion handled by cancelBooking

  // Function to cancel a booking with user confirmation
  const cancelBooking = async (bookingId) => {
    if (!window.confirm("Are you sure you want to cancel this booking?")) {
      return;
    }
    try {
      const b = bookings.find((x) => (x.reservationId || x.id) === bookingId);
      if (b) {
        await removeBookedSeatsFromSchedule({
          hallId: b.hallId || b.hall,
          date: b.date,
          time: b.time || (b.showtime ? String(b.showtime).split(" ")[1] : undefined),
          movieId: b.movieId,
          seats: Array.isArray(b.seats) ? b.seats : [],
        });
      }
      await deleteBooking(bookingId);
    } catch (e) {
      console.error("Error cancelling booking:", e);
    }
  };

  const currentBookings = bookings.filter((booking) => !completedBookings.has(booking.id));
  // const pastBookings = bookings.filter((booking) => completedBookings.has(booking.id));

return (
  <>
    <main className="booking-page">
  {/** Back button removed per request */}

  {currentBookings.length === 0 ? (
        <section className="empty-state" aria-labelledby="empty-bookings-title">
          <figure className="empty-icon" aria-hidden>
            <PiListMagnifyingGlass />
          </figure>
          <h2 id="empty-bookings-title">No bookings yet</h2>
          <p>Browse movies and reserve your seats when you're ready.</p>
          <Link to="/" className="btn primary">Browse movies</Link>
        </section>
      ) : (
        <>
          {/* Current Bookings Section */}
      {currentBookings.length > 0 && <h3 className="section-title">Current Bookings</h3>}
      <section className="booking-list">
              {currentBookings.map((booking) => (
        <article key={booking.id} className="booking-ticket active">
                  <header className="ticket-text">
                    <strong>{booking.movieTitle || booking.movie_name}</strong>
                    <p>
                      <time dateTime={booking.date || undefined}>
                        {(booking.showtime || booking.show_time || `${booking.date ?? ""} ${booking.time ?? ""}`).trim()}
                      </time>
                      {` - Hall: ${booking.hallId || booking.hall}`}
                    </p>
                    <p>
                      Reservation <code>#{booking.reservationId || booking.id}</code>
                    </p>
                    {Array.isArray(booking.seats) && booking.seats.length ? (
                      <>
                        <p style={{ marginBottom: 6 }}>Seats:</p>
                        <ul className="seat-list">
                          {booking.seats.map((s) => (
                            <li key={s}>{s}</li>
                          ))}
                        </ul>
                      </>
                    ) : null}
                  </header>
                  <footer className="ticket-footer">
                    <figure>
                      <QRCodeCanvas value={(booking.reservationId || booking.id).toString()} className="qr-code" aria-label="Reservation QR code" />
                      <figcaption className="visually-hidden">Show this QR code to validate the booking</figcaption>
                    </figure>
          <nav className="ticket-buttons" aria-label="Ticket actions">
                      <button
                        onClick={() => cancelBooking(booking.reservationId || booking.id)}
                        aria-label="Delete booking"
                        title="Delete booking"
                      >
                        <PiTrash />
                      </button>
          </nav>
                  </footer>
        </article>
              ))}
      </section>
        </>
      )}

  </main>
  </>
);
};

export default BookingPage;
